import React, { useState, useMemo, useCallback } from "react";
import { useLeaderboardData, useAchievementCategories } from "@/api/api";
import { Link } from "@tanstack/react-router";
import { User, Tier } from "@/components/mario/marioTypes";
import { ColumnDef, SortingState, PaginationState } from "@tanstack/react-table";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

const Leaderboard: React.FC = () => {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [sorting, setSorting] = useState<SortingState>([]);
    const [filters, setFilters] = useState<{ achievement_category?: string }>({});

    const { data, isLoading } = useLeaderboardData({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sorting,
        filters,
    });

    const { data: categoriesData } = useAchievementCategories({});

    const handleFilterChange = (value: string) => {
        setFilters({ achievement_category: value !== "" ? value : undefined });
        setPagination({ ...pagination, pageIndex: 0 });
    };

    const handlePaginationChange = (newPagination: PaginationState) => {
        setPagination(newPagination);
    };

    const handleSortingChange = (newSorting: SortingState) => {
        setSorting(newSorting);
    };

    const getUnlockedBadges = useCallback(
        (user: User): Tier[] => {
            if (!categoriesData) return [];

            const achievementsByCategory: Record<number, number> = {};

            user.achievements.forEach((achievement) => {
                const categoryId = achievement.category.id;
                const weight = achievement.weight || 1;
                achievementsByCategory[categoryId] = (achievementsByCategory[categoryId] || 0) + weight;
            });

            const unlockedBadges: Tier[] = [];

            categoriesData.results.forEach((category) => {
                const achievementsWeight = achievementsByCategory[category.id] || 0;

                category.tiers.forEach((tier) => {
                    if (achievementsWeight >= tier.required_achievements) {
                        unlockedBadges.push({
                            ...tier,
                            category: category.name,
                        });
                    }
                });
            });

            return unlockedBadges;
        },
        [categoriesData]
    );

    const columns = useMemo<ColumnDef<User>[]>(
        () => [
            {
                accessorKey: "username",
                header: "Username",
                cell: ({ row }) => {
                    const user = row.original;
                    const displayName = user.first_name || user.username;
                    return (
                        <Link to="/achievements/$userId" params={{userId: row.original.id}} className="text-blue-500 hover:underline">
                            {displayName}
                        </Link>
                    );
                },
            },
            {
                accessorKey: "achievement_count",
                header: "Achievements",
                enableSorting: true,
            },
            {
                accessorKey: "badge_count",
                header: "Badge Count",
                enableSorting: true,
                cell: ({ row }) => {
                    const user = row.original;
                    const unlockedBadges = getUnlockedBadges(user);
                    return unlockedBadges.length;
                },
            },
            {
                accessorKey: "badge_images",
                header: "Badges Earned",
                enableSorting: false,
                cell: ({ row }) => {
                    const user = row.original;
                    if (!categoriesData) return null;

                    const unlockedBadges = getUnlockedBadges(user);

                    return (
                        <div className="flex space-x-2">
                            {unlockedBadges.map((badge, index) => (
                                badge.badge_emoji ? (
                                    <span
                                        key={`${user.id}-${badge.level}-${badge.category}-${index}`}
                                        className="text-2xl"
                                        role="img"
                                        aria-label={`Badge Level ${badge.level}`}
                                    >
                                        {badge.badge_emoji}
                                    </span>
                                ) : badge.badge_image ? (
                                    <img
                                        key={`${user.id}-${badge.level}-${badge.category}-${index}`}
                                        src={badge.badge_image}
                                        alt={`Badge Level ${badge.level}`}
                                        className="w-6 h-6"
                                    />
                                ) : null
                            ))}
                        </div>
                    );
                },
            }
        ],
        [categoriesData, getUnlockedBadges]
    );

    return (
        <div>
            <h1 className="text-2xl font-bold mb-4">🍄 DJ's Mario Party</h1>

            <Card className="p-4 shadow-md">
                {isLoading || !categoriesData ? (
                    <Skeleton className="h-10 w-full" />
                ) : (
                    <>
                        <div className="mb-4">
                            <Select onValueChange={handleFilterChange}>
                                <SelectTrigger className="w-64">
                                    <SelectValue placeholder="Filter by Category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="">All Categories</SelectItem>
                                    {categoriesData?.results.map((category) => (
                                        <SelectItem key={category.id} value={category.name}>
                                            {category.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <ThaloDataTable
                            data={data?.results || []}
                            columns={columns}
                            isLoading={isLoading}
                            pageCount={Math.ceil((data?.count || 0) / pagination.pageSize)}
                            pagination={pagination}
                            onPaginationChange={handlePaginationChange}
                            sorting={sorting}
                            onSortingChange={handleSortingChange}
                        />
                    </>
                )}
            </Card>
        </div>
    );
};

export default Leaderboard;